import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// The button stays disabled for the length of the theme's "added" checkmark animation
// (~800ms) so it cannot be double-submitted, then returns to its normal state.
const ADDED_STATE_DURATION = 1000;
const ERROR_STATE_DURATION = 8000;

/**
 * Adds a cross-sold product to the cart from the product page.
 *
 * This deliberately does not reuse `product-form-component`: that component
 * listens for variant events on the surrounding `.shopify-section`, so a second
 * instance living inside the product details would react to the main product's
 * variant changes (its add-to-cart button gets disabled on every variant
 * selection, and its variant id can be overwritten).
 *
 * @typedef {object} CrossSellRefs
 * @property {HTMLElement} liveRegion - Screen reader announcements.
 * @property {HTMLSelectElement} [variantSelect] - Variant picker, when the cross-sold product has several variants.
 * @property {HTMLElement} [errorMessage] - Container for cart error messages.
 *
 * @extends {Component<CrossSellRefs>}
 */
class ShavingartCrossSellComponent extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {number | undefined} */
  #timeout;

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /**
   * The add to cart button rendered by the `add-to-cart-button` snippet. It lives inside
   * `add-to-cart-component`, so it is not exposed through this component's refs.
   *
   * @returns {HTMLButtonElement | null}
   */
  get #button() {
    return this.querySelector('[ref="addToCartButton"]');
  }

  /** @returns {string} */
  get #variantId() {
    const { variantSelect } = this.refs;

    if (variantSelect instanceof HTMLSelectElement) return variantSelect.value;

    return this.dataset.variantId ?? '';
  }

  /**
   * Adds the cross-sold product to the cart.
   *
   * @param {Event} event - The form submit event.
   */
  async handleAdd(event) {
    event.preventDefault();

    const button = this.#button;
    if (button?.disabled) return;

    const variantId = this.#variantId;
    if (!variantId) return;

    if (this.#timeout) clearTimeout(this.#timeout);
    this.dataset.state = 'loading';
    if (button) button.disabled = true;

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', this.dataset.quantity || '1');

    const sections = this.#cartSectionIds();
    if (sections) body.append('sections', sections);

    const config = fetchConfig('javascript', { body });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      // The Cart API answers with a `status` field only when the add failed.
      if (data.status) {
        this.#showError(data.description || data.message || this.dataset.errorText || '');
        return;
      }

      this.dataset.state = 'added';
      this.#announce(this.dataset.addedText || '');

      // `product-form-component` is the source the cart icon treats as "add one more":
      // any other source makes it overwrite the badge with this event's item count.
      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: Number(this.dataset.quantity) || 1,
          productId: this.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      this.#timeout = setTimeout(() => this.#reset(), ADDED_STATE_DURATION);
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.errorText || '');
    }
  }

  /**
   * Section ids the cart needs re-rendered so the drawer reflects the new line item.
   *
   * @returns {string} A comma separated list of section ids.
   */
  #cartSectionIds() {
    const ids = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter((id) => Boolean(id));

    return Array.from(new Set(ids)).join(',');
  }

  /**
   * @param {string} message - The error to surface to the customer.
   */
  #showError(message) {
    const { errorMessage } = this.refs;

    this.dataset.state = 'error';
    if (errorMessage) errorMessage.textContent = message;
    this.#announce(message);

    const button = this.#button;
    if (button) button.disabled = false;

    this.#timeout = setTimeout(() => this.#reset(), ERROR_STATE_DURATION);
  }

  /**
   * @param {string} message - Text announced to screen readers.
   */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }

  #reset() {
    const { errorMessage } = this.refs;

    this.dataset.state = 'idle';
    if (errorMessage) errorMessage.textContent = '';
    this.#announce('');

    const button = this.#button;
    if (button) button.disabled = false;
  }
}

if (!customElements.get('shavingart-cross-sell-component')) {
  customElements.define('shavingart-cross-sell-component', ShavingartCrossSellComponent);
}
